import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Plus, XCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BasicModal } from "@/components/common/BasicModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { CHAT_MESSAGE_MAX_LENGTH, CHAT_TITLE_MAX_LENGTH } from "@/constants";
import { ChatForm } from "@/features/chat/components/ChatForm";
import { ChatRoomListSection } from "@/features/chat/components/ChatRoomListSection";
import { ChatSearchModal } from "@/features/chat/components/ChatSearchModal";
import { RecipientSelectorDialog } from "@/features/chat/components/RecipientSelectorDialog";
import { SelectedRecipients } from "@/features/chat/components/SelectedRecipients";
import { useStickyHeaderOffsets } from "@/features/chat/hooks/useStickyHeaderOffsets";
import {
  type CreateChatFormValues,
  createChatFormSchema,
} from "@/features/chat/schema";
import type { CreateChatModalProps } from "@/features/chat/types";
import { mapChatRoomsToListItems } from "@/features/chat/utils/chatRoomList";
import { buildParticipantIds } from "@/features/chat/utils/participants";
import { useCreateChatRoom } from "@/hooks/useCreateChatRoom";
import { useExistingDmAndTopicChats } from "@/hooks/useExistingDmAndTopicChats";
import { useToast } from "@/hooks/useToast";
import { useUser } from "@/hooks/useUser";
import type { User, UserShort } from "@/lib/humhub-api/types";

// User型をUserShort型に変換するヘルパー関数
const userToUserShort = (user: User): UserShort => ({
  id: user.id,
  guid: user.guid,
  display_name: user.display_name,
  url: user.url,
  image_url: user.image_url,
});

interface EnhancedCreateChatModalProps extends CreateChatModalProps {
  initialUser?: User;
}

export const CreateChatModal = ({
  isOpen,
  onClose,
  onChatCreated,
  initialUser,
}: EnhancedCreateChatModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const {
    createChat,
    isLoading: isSubmitting,
    isError: hasError,
  } = useCreateChatRoom();
  const { currentUser } = useUser();

  const [selectedRecipients, setSelectedRecipients] = useState<UserShort[]>([]);
  const [isRecipientDialogOpen, setIsRecipientDialogOpen] = useState(false);
  const [openSections, setOpenSections] = useState<string[]>([]);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const chatFormSchema = useMemo(() => createChatFormSchema(t), [t]);

  const form = useForm<CreateChatFormValues>({
    resolver: zodResolver(chatFormSchema),
    defaultValues: {
      title: "",
      message: "",
      recipients: [],
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { isDirty, isValid },
  } = form;
  const titleValue = form.watch("title");

  const participantIds = useMemo(
    () => buildParticipantIds(selectedRecipients, currentUser?.id, true),
    [currentUser?.id, selectedRecipients],
  );

  const { existingDmChats, existingTopicChats, isCheckingExisting } =
    useExistingDmAndTopicChats(
      participantIds,
      isOpen && !!currentUser?.id && selectedRecipients.length > 0,
    );
  const hasExistingDm = existingDmChats.length > 0;
  const hasExistingTopics = existingTopicChats.length > 0;
  const hasExistingDmAndTopics = hasExistingDm && hasExistingTopics;
  const showExistingBlock = hasExistingDm;
  const hasRecipients = selectedRecipients.length > 0;
  const messageLabelKey = hasExistingDm
    ? "chat.newTopicLabel"
    : "chat.sendMessage";

  const headerKeys = useMemo(() => {
    const keys: string[] = [];
    if (showExistingBlock) {
      keys.push("existing");
      if (existingDmChats.length > 0) {
        keys.push("existingMessages");
      }
      if (existingTopicChats.length > 0) {
        keys.push("existingTopics");
      }
    }
    keys.push("newConversation");
    return keys;
  }, [existingDmChats.length, existingTopicChats.length, showExistingBlock]);

  const { setHeaderRef, getHeaderOffset } = useStickyHeaderOffsets(headerKeys);

  const openRoom = useCallback(
    (roomId: number) => {
      onClose();
      navigate({ to: `/chat-room/${roomId}` });
    },
    [navigate, onClose],
  );

  const renderTopicList = useCallback(
    (withStickyHeader: boolean) => {
      if (!hasExistingTopics) {
        return null;
      }
      const displayedTopics = existingTopicChats.slice(0, 2);
      const remainingCount = existingTopicChats.length - displayedTopics.length;

      return (
        <div className="space-y-2">
          {withStickyHeader ? (
            <div
              className="sticky z-10 -mx-6 bg-white px-6 py-1 text-xs font-semibold text-text-subtitle"
              style={{ top: getHeaderOffset("existingTopics") }}
              ref={setHeaderRef("existingTopics")}
            >
              {t("chat.existingTopics")}
            </div>
          ) : (
            <div className="text-xs font-semibold text-text-subtitle">
              {t("chat.existingTopics")}
            </div>
          )}
          <ChatRoomListSection
            rooms={mapChatRoomsToListItems(displayedTopics, { useTitle: true })}
            onRoomClick={openRoom}
            listClassName="max-h-[225px] space-y-2 overflow-y-auto pr-1"
          />
          {remainingCount > 0 && (
            <button
              type="button"
              className="w-full rounded-md border border-dashed border-border px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              onClick={() => setIsSearchModalOpen(true)}
            >
              {t("chat.showAllTopics")}
            </button>
          )}
        </div>
      );
    },
    [
      existingTopicChats,
      getHeaderOffset,
      hasExistingTopics,
      openRoom,
      setHeaderRef,
      t,
    ],
  );

  useEffect(() => {
    if (showExistingBlock) {
      setOpenSections([]);
    }
  }, [showExistingBlock]);

  useEffect(() => {
    if (isOpen) {
      const initialRecipients = initialUser
        ? [userToUserShort(initialUser)]
        : [];
      setSelectedRecipients(initialRecipients);
      setOpenSections([]);
      reset({
        title: "",
        message: "",
        recipients: initialRecipients.map((recipient) => recipient.guid),
      });
    }
  }, [isOpen, initialUser, reset]);

  const handleRecipientsSelected = (recipients: UserShort[]) => {
    setSelectedRecipients(recipients);
    setValue(
      "recipients",
      recipients.map((recipient) => recipient.guid),
      { shouldDirty: true, shouldValidate: true },
    );
    setIsRecipientDialogOpen(false);
  };

  const handleRecipientRemove = (recipientToRemove: UserShort) => {
    const updatedRecipients = selectedRecipients.filter(
      (recipient) => recipient.guid !== recipientToRemove.guid,
    );
    setSelectedRecipients(updatedRecipients);
    setValue(
      "recipients",
      updatedRecipients.map((recipient) => recipient.guid),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const handleSearchChatRoomSelect = (roomId: number | undefined) => {
    setIsSearchModalOpen(false);
    if (!roomId) {
      return;
    }
    onClose();
    navigate({ to: `/chat-room/${roomId}` });
  };

  const onSubmitForm = handleSubmit(async (values) => {
    try {
      if (hasExistingDm && values.title.trim().length === 0) {
        setError("title", { type: "manual", message: t("chat.titleRequired") });
        return;
      }

      const response = await createChat({
        title: values.title,
        message: values.message,
        recipient: values.recipients,
      });

      if (onChatCreated) {
        const sectionKey =
          values.title.trim().length === 0 ? "messages" : "topics";
        await onChatCreated(sectionKey);
      }

      toast({
        title: t("chat.createSuccess"),
        description: t("chat.createSuccessDescription"),
      });

      onClose();

      if (response?.id) {
        navigate({
          to: "/chat-room/$chat-room-id",
          params: { "chat-room-id": response.id.toString() },
        });
      }
    } catch (error) {
      console.error("Failed to create chat:", error);
    }
  });

  return (
    <>
      <BasicModal isOpen={isOpen} onClose={onClose} maxWidth="max-w-2xl">
        <Form {...form}>
          <form
            onSubmit={onSubmitForm}
            className="mx-auto max-w-2xl space-y-6 p-6"
          >
            <h2 className="text-center text-2xl font-bold">
              {t("chat.createNewChatRoom")}
            </h2>

            <div className="space-y-6">
              <div className="space-y-2">
                <div className="text-sm font-semibold text-text-subtitle">
                  {t("chat.recipients")}
                </div>
                <div className="flex items-center justify-start">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsRecipientDialogOpen(true)}
                    disabled={isSubmitting}
                  >
                    <Plus size={16} />
                    {t("chat.selectRecipients")}
                  </Button>
                </div>

                <SelectedRecipients
                  recipients={selectedRecipients}
                  onRemove={handleRecipientRemove}
                  showTitle={false}
                />
              </div>

              {hasRecipients && (
                <div className="space-y-6">
                  {isCheckingExisting && (
                    <p className="text-xs text-muted-foreground">
                      {t("chat.checkingExisting")}
                    </p>
                  )}

                  {hasExistingDmAndTopics ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="text-xs font-semibold text-text-subtitle">
                          {t("chat.existingMessages")}
                        </div>
                        <ChatRoomListSection
                          rooms={mapChatRoomsToListItems(existingDmChats)}
                          onRoomClick={openRoom}
                        />
                      </div>

                      <ChatForm
                        form={form}
                        titleMaxLength={CHAT_TITLE_MAX_LENGTH}
                        messageMaxLength={CHAT_MESSAGE_MAX_LENGTH}
                        disabled={isSubmitting}
                        titleMode="required"
                        messageLabelKey={messageLabelKey}
                      />

                      {renderTopicList(false)}
                    </div>
                  ) : showExistingBlock ? (
                    <div className="space-y-4">
                      <div
                        className="sticky z-10 -mx-6 border-b bg-white px-6 py-2 text-sm font-semibold text-text-subtitle"
                        style={{ top: getHeaderOffset("existing") }}
                        ref={setHeaderRef("existing")}
                      >
                        {t("chat.existingConversations")}
                      </div>

                      {existingDmChats.length > 0 && (
                        <div className="space-y-2">
                          <div
                            className="sticky z-10 -mx-6 bg-white px-6 py-1 text-xs font-semibold text-text-subtitle"
                            style={{ top: getHeaderOffset("existingMessages") }}
                            ref={setHeaderRef("existingMessages")}
                          >
                            {t("chat.existingMessages")}
                          </div>
                          <ChatRoomListSection
                            rooms={mapChatRoomsToListItems(existingDmChats)}
                            onRoomClick={openRoom}
                          />
                        </div>
                      )}

                      {renderTopicList(true)}
                    </div>
                  ) : null}

                  {hasExistingDmAndTopics ? null : showExistingBlock ? (
                    <Accordion
                      value={openSections}
                      onValueChange={setOpenSections}
                      className="space-y-0"
                    >
                      <AccordionItem
                        value="new-conversation"
                        className="rounded-none border-0 bg-transparent shadow-none"
                      >
                        <AccordionTrigger
                          className="sticky z-10 -mx-6 bg-white px-6 py-2 text-left text-sm font-semibold text-text-subtitle hover:bg-white"
                          style={{ top: getHeaderOffset("newConversation") }}
                          ref={setHeaderRef("newConversation")}
                        >
                          <div className="flex flex-col gap-1">
                            <span>{t("chat.newConversation")}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="border-0 bg-transparent pt-2">
                          <ChatForm
                            form={form}
                            titleMaxLength={CHAT_TITLE_MAX_LENGTH}
                            messageMaxLength={CHAT_MESSAGE_MAX_LENGTH}
                            disabled={isSubmitting}
                            titleMode="required"
                            messageLabelKey={messageLabelKey}
                          />
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  ) : (
                    <div className="space-y-4">
                      <ChatForm
                        form={form}
                        titleMaxLength={CHAT_TITLE_MAX_LENGTH}
                        messageMaxLength={CHAT_MESSAGE_MAX_LENGTH}
                        disabled={isSubmitting}
                        titleMode="optional"
                        messageLabelKey={messageLabelKey}
                      />
                      {!hasExistingDm && hasExistingTopics
                        ? renderTopicList(false)
                        : null}
                    </div>
                  )}
                </div>
              )}
            </div>

            {hasError && (
              <div className="rounded-md bg-red-50 p-3">
                <div className="flex">
                  <div className="shrink-0">
                    <XCircle className="size-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {t("chat.createError")}
                    </h3>
                    <p className="mt-1 text-sm text-red-700">
                      {t("chat.createErrorDescription")}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                variant="teal"
                disabled={
                  isSubmitting ||
                  !isDirty ||
                  !isValid ||
                  (hasExistingDm && titleValue.trim().length === 0)
                }
              >
                {isSubmitting ? t("common.saving") : t("common.create")}
              </Button>
            </div>
          </form>
        </Form>
      </BasicModal>

      <RecipientSelectorDialog
        open={isRecipientDialogOpen}
        onClose={() => setIsRecipientDialogOpen(false)}
        onRecipientsSelected={handleRecipientsSelected}
        initialRecipients={selectedRecipients}
      />

      <ChatSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onChatRoomSelect={handleSearchChatRoomSelect}
        initialParticipants={selectedRecipients}
        initialSearchMode="participants"
      />
    </>
  );
};
